import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartsDataProvider } from '@mui/x-charts/ChartsDataProvider';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsLegend } from '@mui/x-charts/ChartsLegend';
import { ChartsSurface } from '@mui/x-charts/ChartsSurface';
import { ChartsWrapper } from '@mui/x-charts/ChartsWrapper';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { LineHighlightPlot, LinePlot, MarkPlot } from '@mui/x-charts/LineChart';
import type { BarSeriesType, LineSeriesType } from '@mui/x-charts/models';
import type { FC } from 'react';
import { shortMonthNames } from '../../helpers/months';
import { yAxisBounds } from '../../helpers/y-axis-bounds';
import { yearSeriesId } from './helpers';
import { type PartialMonth, YearOverYearTooltip } from './year-over-year-tooltip';

type ValueFormatter = (value: number | null) => string;

// The legend sits under the plot, flush right, on every dashboard chart.
const LEGEND_POSITION = { horizontal: 'end', vertical: 'bottom' } as const;

// The floor under a filling chart, in px — the same height a chart that asks
// for one gets, so a plot is this tall whether or not it was told to be.
//
// A chart with no height of its own takes the card's leftover room, which works
// while some taller card on the row is setting the height. Alone on a row there
// is no such card, and the card's own height is then whatever its contents ask
// for — so a chart asking for the leftover asks for nothing, and collapses.
// With the floor, the two-chart Inventory Value card stands 726px against a
// stat widget's 722px instead of shrinking to 426px when it wraps onto a row of
// its own.
const MIN_FILL_HEIGHT = 300;

// seriesValues is every plotted number across a set of series, with the gaps
// dropped. What a chart does with them is its own business: the widgets here
// disagree about whether an axis anchors at zero or zooms to the data.
export const seriesValues = (series: (BarSeriesType | LineSeriesType)[]): number[] =>
  series
    .flatMap((s) => s.data ?? [])
    .filter((value): value is number => value !== null && !Number.isNaN(value));

// seriesBounds zooms the axis to what is plotted, so a near-flat series still
// reads as flat. An empty set collapses to zero, since yAxisBounds of nothing
// is ±Infinity.
export const seriesBounds = (
  series: (BarSeriesType | LineSeriesType)[]
): { min: number; max: number } => {
  const values = seriesValues(series);

  return values.length > 0 ? yAxisBounds(values) : { min: 0, max: 0 };
};

// priorYearColor is the blue of an earlier year's line, fading one step per
// year back: index 0 is last year.
export const priorYearColor = (index: number): string => `rgba(59, 130, 246, ${1.0 - index * 0.2})`;

// yearLineSeries is one year's Jan–Dec line, drawn the same way on every
// dashboard chart.
export const yearLineSeries = (
  year: number,
  data: (number | null)[],
  color: string,
  valueFormatter: ValueFormatter
): LineSeriesType => ({
  type: 'line',
  label: `${year}`,
  data,
  id: yearSeriesId(year),
  curve: 'linear',
  color,
  showMark: true,
  labelMarkType: 'square',
  valueFormatter,
});

type YearOverYearChartProperties = {
  series: (BarSeriesType | LineSeriesType)[];
  yAxis: { min: number; max: number; valueFormatter: ValueFormatter };
  // Height in px. Omit it and the chart fills what its card has left over —
  // MUI's own contract for an absent height, and several such charts in one
  // card share the space evenly. Filling needs the card to be a flex column,
  // which WidgetContainer is.
  height?: number;
  hideLegend?: boolean;
  // The month the chart is part-way through, if any. Its bar is a few days of
  // trade next to a full month a year earlier, so the tooltip leaves that one
  // comparison blank rather than reporting a collapse that isn't real.
  partialMonth?: PartialMonth;
  // Set when the plotted values are already percentages, so the tooltip
  // compares them in points instead of as a percentage of a percentage.
  asPoints?: boolean;
};

// YearOverYearChart is the Jan–Dec chart the dashboard widgets share: bars and
// lines over a month band axis, with the dashboard's grid and legend.
//
// ChartsWrapper is not optional chrome. MUI's own charts render it, and the
// surface and the legend are marked for the `chart` and `legend` grid areas it
// declares. Compose the provider's children without it and those area names
// land on whichever element the card happens to put around them — two charts
// in one card then share a single area and draw on top of each other. It also
// derives the fill behaviour: given no height, the plot takes the leftover row
// and the legend keeps exactly what it needs.
export const YearOverYearChart: FC<YearOverYearChartProperties> = ({
  series,
  yAxis,
  height,
  hideLegend = false,
  partialMonth,
  asPoints,
}) => (
  <ChartsDataProvider
    // No width, for the same reason as the height below: the chart takes the
    // card's, and the card takes its dashboard grid column's. Nothing on this
    // path carries a fixed px width, so a card is as wide as the layout gives
    // it at any viewport.
    height={height}
    series={series}
    xAxis={[{ data: shortMonthNames, scaleType: 'band' }]}
    yAxis={[
      {
        scaleType: 'linear',
        min: yAxis.min,
        max: yAxis.max,
        tickNumber: 5,
        valueFormatter: yAxis.valueFormatter,
        // 'auto' measures the tick labels instead of trusting a constant. A
        // fixed 80px fits "$700,000" and cuts the leading digits off a store
        // whose inventory runs to "$2,600,000" — and how wide the widest label
        // is depends on the store, the metric and the cost/retail toggle, so
        // there is no constant that is right for all of them.
        width: 'auto',
      },
    ]}
    // No negative left margin with an auto-width axis: that pulled the plot
    // 20px back over a fixed-width axis to reclaim its slack, and against an
    // axis already measured to fit it would clip the labels it just sized for.
    margin={{ left: 0, right: 0, top: 20, bottom: 10 }}
  >
    <ChartsWrapper
      hideLegend={hideLegend}
      legendDirection="horizontal"
      legendPosition={LEGEND_POSITION}
      // ChartsWrapper is flex: 1, so a filling chart grows with its card. One
      // given a height of its own keeps it instead of stretching; one without
      // still never drops below the floor, so it survives a card that has no
      // height to share.
      sx={height === undefined ? { minHeight: MIN_FILL_HEIGHT } : { flex: 'none' }}
    >
      <ChartsSurface>
        <BarPlot />
        <LinePlot />
        <MarkPlot slotProps={{ mark: { shape: 'circle' } }} />
        <LineHighlightPlot />
        <YearOverYearTooltip partialMonth={partialMonth} asPoints={asPoints} />
        <ChartsXAxis />
        <ChartsYAxis />
        <ChartsGrid
          horizontal
          classes={{
            horizontalLine: 'opacity-50',
          }}
        />
      </ChartsSurface>
      {!hideLegend && (
        <ChartsLegend
          direction="horizontal"
          slotProps={{
            legend: {
              sx: {
                justifyContent: 'flex-end',
                marginRight: '0',
                fontSize: '1rem',
                fontWeight: 'Normal',
                '& .MuiChartsLegend-mark': {
                  marginRight: '1px',
                  transform: 'scale(1.3)',
                },
                '& .MuiChartsLegend-series': {
                  gap: '6px',
                },
              },
            },
          }}
        />
      )}
    </ChartsWrapper>
  </ChartsDataProvider>
);
